(() => {
	const started = new WeakSet();

	const describe = (count, rating, total) => {
		const whole = Math.max(count, total || 0);
		const reviews = whole === 1 ? 'review' : 'reviews';
		const amount = `${count} of ${whole}`;
		if (!rating) return `Showing ${amount} ${reviews}`;
		return `Showing ${amount} ${reviews} with ${rating} ${rating === 1 ? 'star' : 'stars'}`;
	};

	const httpish = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

	const tidy = (value) => String(value === null || value === undefined ? '' : value)
		.replace(/\r/g, '')
		.replace(/[ \t]+\n/g, '\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();

	const decode = (value) => {
		const box = document.createElement('textarea');
		box.innerHTML = String(value === null || value === undefined ? '' : value);
		return box.value;
	};

	const stamp = (value) => {
		const when = new Date(value);
		if (Number.isNaN(when.getTime())) return '';
		return `${when.getDate()} ${when.toLocaleString('en', { month: 'long' })} ${when.getFullYear()}`;
	};

	const normalise = (review) => ({
		rating: Number(review.rating) || 0,
		title: review.title || '',
		body: String(review.body || review.body_html || '').replace(/<[^>]*>/g, '').trim(),
		name: review.reviewer_name || '',
		date: stamp(review.created_at),
		when: Date.parse(review.created_at) || 0,
		verified: Boolean(review.verified_buyer),
		product: review.product_title || '',
		url: review.product_url || '',
		store: Boolean(review.is_shop_review),
		pictures: Array.isArray(review.pictures_urls) ? review.pictures_urls : [],
		videos: Array.isArray(review.video_external_ids) ? review.video_external_ids : [],
		hosted: Array.isArray(review.media_platform_hosted_video_infos) ? review.media_platform_hosted_video_infos : [],
		reply: review.reply_content || null
	});

	const el = (tag, className, text) => {
		const node = document.createElement(tag);
		if (className) node.className = className;
		if (text) node.textContent = text;
		return node;
	};

	const viewerOpen = () => Boolean(document.querySelector('.nd-review-viewer:not([hidden])'));

	const viewer = (() => {
		let root = null;
		let frame = null;
		let image = null;
		let prev = null;
		let next = null;
		let shots = [];
		let at = 0;
		let opener = null;

		function close() {
			if (!root || root.hidden) return;
			root.hidden = true;
			if (opener && typeof opener.focus === 'function') opener.focus();
		}

		const paint = () => {
			image.src = shots[at] || '';
			const many = shots.length > 1;
			prev.hidden = !many;
			next.hidden = !many;
			root.setAttribute('aria-label', many ? `Review photo ${at + 1} of ${shots.length}` : 'Review photo');
		};

		const step = (delta) => {
			if (shots.length < 2) return;
			at = (at + delta + shots.length) % shots.length;
			paint();
		};

		const build = () => {
			if (root) return;
			root = el('div', 'nd-review-viewer');
			root.hidden = true;
			root.setAttribute('role', 'dialog');
			root.setAttribute('aria-modal', 'true');
			const backdrop = el('div', 'nd-review-viewer__backdrop');
			backdrop.addEventListener('click', close);
			frame = el('div', 'nd-review-viewer__frame');
			image = el('img', 'nd-review-viewer__image');
			image.alt = '';
			const shut = el('button', 'nd-review-dialog__close nd-review-viewer__close');
			shut.type = 'button';
			shut.setAttribute('aria-label', 'Close photo');
			shut.addEventListener('click', close);
			prev = el('button', 'nd-review-dialog__nav nd-review-dialog__nav--prev');
			prev.type = 'button';
			prev.setAttribute('aria-label', 'Previous photo');
			prev.addEventListener('click', () => step(-1));
			next = el('button', 'nd-review-dialog__nav nd-review-dialog__nav--next');
			next.type = 'button';
			next.setAttribute('aria-label', 'Next photo');
			next.addEventListener('click', () => step(1));
			let swipeFrom = 0;
			let swipeUp = 0;
			let swiping = false;
			let swiped = false;

			frame.addEventListener('pointerdown', (event) => {
				if (event.pointerType === 'mouse' && event.button !== 0) return;
				if (event.target.closest('button, a[href]')) return;
				swiping = true;
				swiped = false;
				swipeFrom = event.clientX;
				swipeUp = event.clientY;
			});

			frame.addEventListener('pointermove', (event) => {
				if (!swiping) return;
				const shift = event.clientX - swipeFrom;
				const rise = event.clientY - swipeUp;
				if (!swiped && Math.abs(shift) > 10 && Math.abs(shift) > Math.abs(rise)) {
					swiped = true;
					frame.classList.add('is-dragging');
					if (typeof frame.setPointerCapture === 'function') {
						try { frame.setPointerCapture(event.pointerId); } catch (error) { swiped = true; }
					}
				}
				if (swiped) event.preventDefault();
			});

			['pointerup', 'pointercancel'].forEach((name) => {
				frame.addEventListener(name, (event) => {
					if (!swiping) return;
					swiping = false;
					frame.classList.remove('is-dragging');
					if (!swiped) return;
					const shift = event.clientX - swipeFrom;
					if (Math.abs(shift) >= 60) step(shift < 0 ? 1 : -1);
				});
			});

			frame.addEventListener('click', (event) => {
				if (!swiped) return;
				swiped = false;
				event.preventDefault();
				event.stopPropagation();
			}, true);

			frame.append(image, shut, prev, next);
			root.append(backdrop, frame);
			document.body.appendChild(root);
			document.addEventListener('keydown', (event) => {
				if (root.hidden) return;
				if (event.key === 'Escape') close();
				if (event.key === 'ArrowLeft') step(-1);
				if (event.key === 'ArrowRight') step(1);
			});
		};

		const open = (list, index) => {
			const usable = (list || []).filter(Boolean);
			if (!usable.length) return;
			build();
			shots = usable;
			at = Math.min(Math.max(0, index || 0), shots.length - 1);
			opener = document.activeElement;
			paint();
			root.hidden = false;
		};

		return { open };
	})();

	const lightbox = (() => {
		let root = null;
		let panel = null;
		let content = null;
		let prev = null;
		let next = null;
		let items = [];
		let order = [];
		let at = 0;
		let opener = null;

		const make = (tag, className, text) => {
			const node = document.createElement(tag);
			if (className) node.className = className;
			if (text) node.textContent = text;
			return node;
		};

		function close() {
			if (!root || root.hidden) return;
			root.hidden = true;
			document.documentElement.style.overflow = '';
			if (window.lenis && typeof window.lenis.start === 'function') window.lenis.start();
			if (opener) opener.focus();
		}

		const step = (delta) => {
			if (order.length < 2) return;
			at = (at + delta + order.length) % order.length;
			render();
		};

		const build = () => {
			if (root) return;
			root = make('div', 'nd-review-dialog');
			root.hidden = true;
			const backdrop = make('div', 'nd-review-dialog__backdrop');
			backdrop.addEventListener('click', close);
			panel = make('div', 'nd-review-dialog__panel');
			panel.setAttribute('role', 'dialog');
			panel.setAttribute('aria-modal', 'true');
			panel.setAttribute('tabindex', '-1');
			panel.setAttribute('data-lenis-prevent', '');
			const shut = make('button', 'nd-review-dialog__close');
			shut.type = 'button';
			shut.setAttribute('aria-label', 'Close');
			shut.addEventListener('click', close);
			content = make('div', 'nd-review-dialog__content');
			prev = make('button', 'nd-review-dialog__nav nd-review-dialog__nav--prev');
			prev.type = 'button';
			prev.setAttribute('aria-label', 'Previous review');
			prev.addEventListener('click', () => step(-1));
			next = make('button', 'nd-review-dialog__nav nd-review-dialog__nav--next');
			next.type = 'button';
			next.setAttribute('aria-label', 'Next review');
			next.addEventListener('click', () => step(1));
			let swipeFrom = 0;
			let swipeUp = 0;
			let swiping = false;
			let swiped = false;

			panel.addEventListener('pointerdown', (event) => {
				if (event.pointerType === 'mouse' && event.button !== 0) return;
				if (event.target.closest('button, a[href]')) return;
				swiping = true;
				swiped = false;
				swipeFrom = event.clientX;
				swipeUp = event.clientY;
			});

			panel.addEventListener('pointermove', (event) => {
				if (!swiping) return;
				const shift = event.clientX - swipeFrom;
				const rise = event.clientY - swipeUp;
				if (!swiped && Math.abs(shift) > 10 && Math.abs(shift) > Math.abs(rise)) {
					swiped = true;
					panel.classList.add('is-dragging');
					if (typeof panel.setPointerCapture === 'function') {
						try { panel.setPointerCapture(event.pointerId); } catch (error) { swiped = true; }
					}
				}
				if (swiped) event.preventDefault();
			});

			['pointerup', 'pointercancel'].forEach((name) => {
				panel.addEventListener(name, (event) => {
					if (!swiping) return;
					swiping = false;
					panel.classList.remove('is-dragging');
					if (!swiped) return;
					const shift = event.clientX - swipeFrom;
					if (Math.abs(shift) >= 60) step(shift < 0 ? 1 : -1);
				});
			});

			panel.addEventListener('click', (event) => {
				if (!swiped) return;
				swiped = false;
				event.preventDefault();
				event.stopPropagation();
			}, true);

			panel.append(shut, content, prev, next);
			root.append(backdrop, panel);
			document.body.appendChild(root);
			document.addEventListener('keydown', (event) => {
				if (root.hidden || viewerOpen()) return;
				if (event.key === 'Escape') close();
				if (event.key === 'ArrowLeft') step(-1);
				if (event.key === 'ArrowRight') step(1);
			});
		};

		const media = (item) => {
			const box = make('div', 'nd-review-dialog__media');
			const sources = [];
			(Array.isArray(item.pictures) ? item.pictures : []).forEach((picture) => {
				const src = typeof picture === 'string' ? picture : picture && (picture.huge || picture.original || picture.url || picture.small);
				if (!httpish(src)) return;
				const spot = sources.length;
				sources.push(src);
				const image = make('img', 'nd-review-dialog__picture');
				image.src = src;
				image.alt = '';
				image.loading = 'lazy';
				image.tabIndex = 0;
				image.setAttribute('role', 'button');
				image.setAttribute('aria-label', 'Open photo');
				image.addEventListener('click', (event) => {
					event.stopPropagation();
					viewer.open(sources, spot);
				});
				image.addEventListener('keydown', (event) => {
					if (event.key !== 'Enter' && event.key !== ' ') return;
					event.preventDefault();
					event.stopPropagation();
					viewer.open(sources, spot);
				});
				box.appendChild(image);
			});
			(Array.isArray(item.videos) ? item.videos : []).forEach((video) => {
				const id = typeof video === 'string' ? video : video && (video.external_id || video.id);
				if (!id || !/^[\w-]{6,20}$/.test(id)) return;
				const frame = make('iframe', 'nd-review-dialog__video');
				frame.src = `https://www.youtube.com/embed/${id}`;
				frame.title = 'Review video';
				frame.loading = 'lazy';
				frame.setAttribute('allowfullscreen', '');
				box.appendChild(frame);
			});
			(Array.isArray(item.hosted) ? item.hosted : []).forEach((info) => {
				const src = info && (info.url || info.video_url || info.mp4_url);
				if (!httpish(src)) return;
				const video = make('video', 'nd-review-dialog__video');
				video.src = src;
				video.controls = true;
				video.playsInline = true;
				box.appendChild(video);
			});
			return box.childElementCount ? box : null;
		};

		function render() {
			const card = order[at];
			if (!card) return;
			const item = items[Number(card.getAttribute('data-nd-card-index'))] || {};
			content.replaceChildren();
			const head = make('div', 'nd-review-dialog__head');
			const stars = card.querySelector('.nd-review-card__stars');
			if (stars) head.appendChild(stars.cloneNode(true));
			if (item.title) head.appendChild(make('p', 'nd-review-dialog__title heading heading--size--h6', item.title));
			const bits = [item.name, item.date].filter(Boolean);
			if (item.verified) bits.push('Verified buyer');
			if (bits.length) head.appendChild(make('p', 'nd-review-dialog__meta text text--size--sm', bits.join(' · ')));
			content.appendChild(head);
			const prose = tidy(item.body);
			if (prose) content.appendChild(make('p', 'nd-review-dialog__body text', prose));
			const shots = media(item);
			if (shots) content.appendChild(shots);
			if (item.reply) {
				const reply = make('div', 'nd-review-dialog__reply');
				reply.appendChild(make('p', 'nd-review-dialog__reply-label text text--size--sm', 'Reply from the store'));
				reply.appendChild(make('p', 'nd-review-dialog__reply-body text text--size--sm', String(item.reply).replace(/<[^>]*>/g, '')));
				content.appendChild(reply);
			}
			if (item.store) {
				content.appendChild(make('p', 'nd-review-dialog__product text text--size--xs', 'Review of the store'));
			} else if (item.product) {
				const line = make('p', 'nd-review-dialog__product text text--size--xs');
				const name = decode(item.product);
				if (item.url && item.url !== '/') {
					line.append('about ');
					const link = make('a', 'nd-review-dialog__link', name);
					link.href = item.url;
					line.appendChild(link);
				} else {
					line.textContent = `about ${name}`;
				}
				content.appendChild(line);
			}
			prev.hidden = order.length < 2;
			next.hidden = order.length < 2;
			panel.setAttribute('aria-label', `Review by ${item.name || 'a customer'}`);
			panel.scrollTop = 0;
		}

		const open = (list, card, data) => {
			build();
			items = data;
			order = list;
			at = Math.max(0, list.indexOf(card));
			opener = card;
			render();
			root.hidden = false;
			document.documentElement.style.overflow = 'hidden';
			if (window.lenis && typeof window.lenis.stop === 'function') window.lenis.stop();
			panel.focus();
		};

		return { open };
	})();

	const setup = (root) => {
		if (started.has(root)) return;
		started.add(root);
		const track = root.querySelector('[data-nd-cards-track]');
		const prev = root.querySelector('[data-nd-cards-prev]');
		const next = root.querySelector('[data-nd-cards-next]');
		if (!track) return;
		const section = root.closest('.shopify-section') || document;
		const scope = section.querySelector('[data-nd-reviews]') || section;
		const label = section.querySelector('[data-nd-reviews-status-label]');
		const live = section.querySelector('[data-nd-reviews-live]');
		const buttons = [...section.querySelectorAll('[data-nd-rating]')];
		const cards = [...track.children];
		const ratingOf = (card) => Number(card.getAttribute('data-nd-card-rating'));
		const shown = () => cards.filter((card) => !card.hidden);

		let data = [];
		const source = root.querySelector('[data-nd-cards-data]');
		if (source) {
			try {
				data = JSON.parse(source.textContent) || [];
			} catch (error) {
				data = [];
			}
		}
		const attach = (card) => {
			const reveal = () => lightbox.open(shown(), card, data);
			card.addEventListener('click', (event) => {
				if (event.target.closest('a[href]')) return;
				reveal();
			});
			card.addEventListener('keydown', (event) => {
				if (event.key !== 'Enter' && event.key !== ' ') return;
				if (event.target.closest('a[href]')) return;
				event.preventDefault();
				reveal();
			});
		};

		if (data.length) cards.forEach(attach);

		const step = () => {
			const card = shown()[0];
			if (!card) return track.clientWidth;
			const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
			return card.getBoundingClientRect().width + gap;
		};

		const columns = () => parseFloat(getComputedStyle(root).getPropertyValue('--nd-cards-columns')) || 1;

		const update = () => {
			const max = track.scrollWidth - track.clientWidth;
			const scrollable = max > 16 && shown().length > columns();
			track.toggleAttribute('data-nd-cards-centered', max <= 16);
			[prev, next].forEach((button) => {
				if (!button) return;
				button.hidden = !scrollable;
			});
			if (prev) prev.disabled = track.scrollLeft <= 2;
			if (next) next.disabled = track.scrollLeft >= max - 2;
		};

		let dragging = false;
		let dragged = false;
		let originX = 0;
		let originLeft = 0;

		const endDrag = () => {
			if (!dragging) return;
			dragging = false;
			track.classList.remove('is-dragging');
			track.style.removeProperty('scroll-snap-type');
			track.style.removeProperty('scroll-behavior');
		};

		track.addEventListener('pointerdown', (event) => {
			if (event.pointerType !== 'mouse' || event.button !== 0) return;
			if (event.target.closest('a[href], button')) return;
			dragging = true;
			dragged = false;
			originX = event.clientX;
			originLeft = track.scrollLeft;
		});

		track.addEventListener('pointermove', (event) => {
			if (!dragging) return;
			const shift = event.clientX - originX;
			if (!dragged && Math.abs(shift) < 6) return;
			if (!dragged) {
				dragged = true;
				track.classList.add('is-dragging');
				track.style.setProperty('scroll-snap-type', 'none');
				track.style.setProperty('scroll-behavior', 'auto');
				if (typeof track.setPointerCapture === 'function') {
					try { track.setPointerCapture(event.pointerId); } catch (error) { dragged = true; }
				}
			}
			event.preventDefault();
			track.scrollLeft = originLeft - shift;
		});

		['pointerup', 'pointercancel', 'pointerleave'].forEach((name) => {
			track.addEventListener(name, endDrag);
		});

		track.addEventListener('click', (event) => {
			if (!dragged) return;
			dragged = false;
			event.preventDefault();
			event.stopPropagation();
		}, true);

		if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
		if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
		track.addEventListener('scroll', update, { passive: true });
		new ResizeObserver(update).observe(track);
		update();

		const counts = {};
		const totals = {};
		const tally = () => {
			Object.keys(counts).forEach((key) => delete counts[key]);
			cards.forEach((card) => {
				const rating = ratingOf(card);
				counts[rating] = (counts[rating] || 0) + 1;
			});
		};
		tally();
		buttons.forEach((button) => {
			totals[Number(button.getAttribute('data-nd-rating'))] = Number(button.getAttribute('data-nd-total')) || 0;
		});
		const totalAll = Object.keys(totals).reduce((sum, key) => sum + totals[key], 0);
		let current = 0;

		const show = (rating) => {
			const count = rating ? counts[rating] || 0 : cards.length;
			if (!count) return false;
			current = rating;
			cards.forEach((card) => {
				card.hidden = Boolean(rating) && ratingOf(card) !== rating;
			});
			track.scrollTo({ left: 0, behavior: 'auto' });
			update();
			buttons.forEach((button) => {
				if (!button.disabled) button.setAttribute('aria-pressed', String(Number(button.getAttribute('data-nd-rating')) === rating));
			});
			scope.toggleAttribute('data-nd-filtered', rating > 0);
			const total = rating ? totals[rating] || 0 : totalAll;
			if (label) label.textContent = rating ? describe(count, rating, total) : '';
			if (live) live.textContent = describe(count, rating, total);
			if (rating && count < total) load();
			return true;
		};

		const arm = () => {
			buttons.forEach((button) => {
				const rating = Number(button.getAttribute('data-nd-rating'));
				if (!counts[rating] || button.dataset.ndArmed) return;
				button.dataset.ndArmed = '1';
				button.disabled = false;
				button.setAttribute('aria-pressed', 'false');
				button.addEventListener('click', () => {
					load();
					show(current === rating ? 0 : rating);
				});
			});
		};
		arm();

		const ceiling = Number(root.getAttribute('data-nd-cards-max')) || 0;
		const shopDomain = root.getAttribute('data-nd-cards-shop') || '';
		const shopPage = root.getAttribute('data-nd-cards-page') || '';
		const onlyProduct = root.getAttribute('data-nd-cards-only') || '';
		const linkProducts = root.hasAttribute('data-nd-cards-product');
		const template = cards[0];
		let loading = false;
		const productList = (root.getAttribute('data-nd-cards-products') || '').split(',').map((entry) => entry.trim()).filter(Boolean);
		let cursor = 0;
		let page = 1;
		let loaded = !ceiling || !shopDomain || !template || !data.length || !productList.length || cards.length >= ceiling;
		const spin = (on) => {
			if (!next) return;
			if (on) {
				next.setAttribute('data-nd-loading', '');
				next.setAttribute('aria-busy', 'true');
			} else {
				next.removeAttribute('data-nd-loading');
				next.removeAttribute('aria-busy');
			}
		};

		const keyOf = (item) => `${String(item.name || '').trim()}|${item.rating}|${String(item.body || '').replace(/\s+/g, ' ').trim().slice(0, 60)}`;
		const seen = new Set(data.map(keyOf));

		const draw = (item, index) => {
			const card = template.cloneNode(true);
			card.hidden = false;
			card.setAttribute('data-nd-card-rating', String(item.rating));
			card.setAttribute('data-nd-card-index', String(index));
			const text = card.querySelector('.nd-review-card__text');
			if (text) text.textContent = item.body;
			const stars = card.querySelector('.nd-review-card__stars');
			if (stars) stars.style.setProperty('--nd-reviews-fill', `${item.rating * 20}%`);
			const reader = card.querySelector('.nd-review-card__meta .visually-hidden');
			if (reader) reader.textContent = `${item.rating} out of 5 stars. `;
			const name = card.querySelector('.nd-review-card__name');
			if (name) name.textContent = item.name;
			let link = card.querySelector('.nd-review-card__product');
			const wants = linkProducts && !item.store && item.product && item.url && item.url !== '/';
			if (wants) {
				if (!link) {
					link = document.createElement('a');
					link.className = 'nd-review-card__product text text--size--xs';
					(name && name.parentNode ? name.parentNode : card).appendChild(link);
				}
				link.href = item.url;
				link.textContent = decode(item.product);
			} else if (link) {
				link.remove();
			}
			return card;
		};

		const absorb = (list) => {
			const fresh = [];
			list.forEach((raw) => {
				if (cards.length + fresh.length >= ceiling) return;
				const item = normalise(raw);
				if (!item.body || !item.rating) return;
				if (onlyProduct && item.url !== onlyProduct) return;
				const key = keyOf(item);
				if (seen.has(key)) return;
				seen.add(key);
				fresh.push(item);
			});
			if (!fresh.length) return 0;
			fresh.sort((a, b) => b.when - a.when);
			const batch = document.createDocumentFragment();
			fresh.forEach((item) => {
				const card = draw(item, data.push(item) - 1);
				if (current && item.rating !== current) card.hidden = true;
				cards.push(card);
				attach(card);
				batch.appendChild(card);
			});
			track.appendChild(batch);
			tally();
			arm();
			if (current) show(current);
			else update();
			return fresh.length;
		};

		const widgetUrl = (id) => {
			const query = new URLSearchParams({
				url: shopDomain,
				shop_domain: shopDomain,
				platform: 'shopify',
				product_id: id,
				page: String(page),
				per_page: '30'
			});
			return `https://cdn.judge.me/reviews/reviews_for_widget?${query}`;
		};

		function load() {
			if (loaded || loading) return;
			if (cursor >= productList.length || cards.length >= ceiling) {
				loaded = true;
				return;
			}
			loading = true;
			spin(true);
			let added = 0;
			fetch(widgetUrl(productList[cursor]), { credentials: 'omit' })
				.then((response) => (response.ok ? response.json() : null))
				.then((payload) => {
					if (!payload) {
						cursor += 1;
						page = 1;
						return;
					}
					added = absorb(payload.reviews || []);
					const paging = payload.pagination || {};
					if (page < (Number(paging.total_pages) || 1)) {
						page += 1;
					} else {
						cursor += 1;
						page = 1;
					}
				})
				.catch(() => {
					cursor += 1;
					page = 1;
				})
				.finally(() => {
					loading = false;
					if (cursor >= productList.length || cards.length >= ceiling) loaded = true;
					const again = !added && !loaded;
					if (!again) spin(false);
					if (again) load();
				});
		}

		const nearEnd = () => {
			const max = track.scrollWidth - track.clientWidth;
			if (max <= 16) return false;
			return max - track.scrollLeft <= step() * 2 + 8;
		};

		track.addEventListener('scroll', () => {
			if (nearEnd()) load();
		}, { passive: true });

		section.querySelectorAll('[data-nd-reviews-reset]').forEach((reset) => {
			reset.addEventListener('click', () => {
				const previous = buttons.find((button) => Number(button.getAttribute('data-nd-rating')) === current);
				if (show(0) && previous) previous.focus();
			});
		});
	};

	const refresh = () => document.querySelectorAll('[data-nd-cards]').forEach(setup);

	refresh();
	new MutationObserver(refresh).observe(document.body, { childList: true, subtree: true });
})();
